import React from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled, { createGlobalStyle } from 'styled-components';

import { useBoolean } from 'app/hooks';

import { ReactComponent as IconDate } from 'app/assets/icons/icon-date.svg';

import clsx from 'clsx';

const Label = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 23px;
  color: var(--gray-700);
`;
const Main = styled.div`
  margin-bottom: 24px;
  position: relative;
  .form-control.form-control-solid:focus {
    background: var(--neutral-2);
  }
  .form-control {
    font-weight: 400;
    font-size: 16px;
    line-height: 26px;
    height: 43px;
    color: var(--neutral-10) !important;
    background: var(--neutral-2) !important;
  }
  .react-datepicker-popper {
    background: var(--white);
    box-shadow: 23.4866px 32.3787px 94px rgba(25, 36, 64, 0.1),
      4.69732px 6.47574px 15.275px rgba(25, 36, 64, 0.05);
    border-radius: 8px;
    padding-top: 0px !important;
  }
  .react-datepicker {
    border: none;
    padding: 10px 20px;
    margin-top: 10px;
  }
  .react-datepicker__day {
    width: 33px;
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    color: var(--neutral-10);
    padding: 5px 0px;
    margin: 0px;
  }
  .react-datepicker__day--selected {
    color: var(--white) !important;
    font-weight: 600;
    background-color: var(--primary) !important;
    outline: none !important;
    border-radius: 8px;
  }
  .react-datepicker__day--keyboard-selected {
    color: var(--neutral-10);
    font-weight: 400;
    background-color: var(--white);
    outline: none !important;
    border-radius: 8px;
  }
  .react-datepicker__day--today {
    color: var(--primary);
    font-weight: 600;
    font-size: 14px;
    line-height: 23px;
  }
  .react-datepicker__header {
    border: none;
    background-color: var(--white);
    padding: 0px;
  }
  .react-datepicker__current-month {
    font-weight: 400;
    font-size: 16px;
    line-height: 26px;
    color: #121212;
  }
  .react-datepicker__day-name {
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: var(--neutral-7);
  }
  .react-datepicker__day-names {
    display: flex;
    justify-content: space-between;
    padding: 0px 4px;
    margin-bottom: 10px;
    margin-top: 12px;
  }
  .react-datepicker__month {
    margin: 0px;
  }
  .react-datepicker__navigation {
    width: 32px;
    height: 32px;
    background: var(--neutral-2-5);
    border-radius: 8px;
    top: 14px;
  }
  .react-datepicker__navigation--previous {
    left: 20px;
  }
  .react-datepicker__navigation--next {
    right: 20px;
  }
  .react-datepicker__navigation-icon--previous {
    right: 0px;
    top: 5px;
  }
  .react-datepicker__navigation-icon--next {
    left: 0px;
    top: 5px;
  }
  .react-datepicker__navigation-icon::before {
    border-width: 1.5px 1.5px 0 0;
    border-color: var(--neutral-7);
    height: 7px;
    top: 7px;
    width: 7px;
  }
  .react-datepicker__navigation-icon--previous::before {
    right: -6px;
  }
  .react-datepicker__navigation-icon--next::before {
    left: -6px;
  }
  .react-datepicker__day--disabled {
    color: var(--neutral-6) !important;
  }
  .react-datepicker__day:hover {
    background: var(--neutral-2);
    border-radius: 8px;
  }
  .react-datepicker-wrapper {
    width: 100%;
  }

  /* Month and Year dropdown styles */
  .react-datepicker__month-select,
  .react-datepicker__year-select {
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    color: var(--neutral-10);
    padding: 5px;
    border-radius: 4px;
    border: 1px solid var(--neutral-3);
    background-color: var(--white);
  }

  /* Year dropdown specific styles */
  .react-datepicker__year-dropdown {
    background-color: var(--white);
    border: 1px solid var(--neutral-3);
    border-radius: 8px;
    max-height: 200px;
    overflow-y: auto;
    width: 120px !important;
    left: 0;
    position: absolute;
    z-index: 1;
  }

  .react-datepicker__year-dropdown-container {
    position: relative;
  }

  .react-datepicker__year-option {
    padding: 8px 12px;
    cursor: pointer;
    color: var(--neutral-10);
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
  }

  .react-datepicker__year-option:hover {
    background-color: var(--neutral-2);
  }

  .react-datepicker__year-option--selected {
    display: none;
    background-color: var(--primary);
    color: var(--white);
  }

  .react-datepicker__year-option--selected_year {
    font-weight: 600;
  }

  /* Hide the checkbox in year dropdown */
  .react-datepicker__year-option .react-datepicker__navigation--years {
    display: inline-block;
    height: 10px;
    width: 10px;
    position: relative;
  }

  .react-datepicker__year-option .react-datepicker__navigation--years-upcoming {
    top: -4px;
  }

  .react-datepicker__year-option .react-datepicker__navigation--years-previous {
    top: 4px;
  }

  .react-datepicker__year-option--selected::before {
    display: none;
  }

  /* Hide the checkbox */
  .react-datepicker__year-option input[type='checkbox'] {
    display: none;
  }
`;

const DatePickerGlobalStyles = createGlobalStyle`
  #datepicker-portal .react-datepicker-popper {
    background: var(--white);
    box-shadow: 23.4866px 32.3787px 94px rgba(25, 36, 64, 0.1),
      4.69732px 6.47574px 15.275px rgba(25, 36, 64, 0.05);
    border-radius: 8px;
    padding-top: 0px !important;
    z-index: 1000;
  }
  #datepicker-portal .react-datepicker {
    border: none;
    padding: 10px 20px;
    margin-top: 10px;
  }
  #datepicker-portal .react-datepicker__day {
    width: 33px;
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    color: var(--neutral-10);
    padding: 5px 0px;
    margin: 0px;
  }
  #datepicker-portal .react-datepicker__day--selected {
    color: var(--white) !important;
    font-weight: 600;
    background-color: var(--primary) !important;
    outline: none !important;
    border-radius: 8px;
  }
  #datepicker-portal .react-datepicker__day--keyboard-selected {
    color: var(--neutral-10);
    font-weight: 400;
    background-color: var(--white);
    outline: none !important;
    border-radius: 8px;
  }
  #datepicker-portal .react-datepicker__day--today {
    color: var(--primary);
    font-weight: 600;
    font-size: 14px;
    line-height: 23px;
  }
  #datepicker-portal .react-datepicker__header {
    border: none;
    background-color: var(--white);
    padding: 0px;
  }
  #datepicker-portal .react-datepicker__current-month {
    font-weight: 400;
    font-size: 16px;
    line-height: 26px;
    color: #121212;
  }
  #datepicker-portal .react-datepicker__day-name {
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: var(--neutral-7);
  }
  #datepicker-portal .react-datepicker__day-names {
    display: flex;
    justify-content: space-between;
    padding: 0px 4px;
    margin-bottom: 10px;
    margin-top: 12px;
  }
  #datepicker-portal .react-datepicker__month {
    margin: 0px;
  }
  #datepicker-portal .react-datepicker__navigation {
    width: 32px;
    height: 32px;
    background: var(--neutral-2-5);
    border-radius: 8px;
    top: 14px;
  }
  #datepicker-portal .react-datepicker__navigation--previous {
    left: 20px;
  }
  #datepicker-portal .react-datepicker__navigation--next {
    right: 20px;
  }
  #datepicker-portal .react-datepicker__navigation-icon--previous {
    right: 0px;
    top: 5px;
  }
  #datepicker-portal .react-datepicker__navigation-icon--next {
    left: 0px;
    top: 5px;
  }
  #datepicker-portal .react-datepicker__navigation-icon::before {
    border-width: 1.5px 1.5px 0 0;
    border-color: var(--neutral-7);
    height: 7px;
    top: 7px;
    width: 7px;
  }
  #datepicker-portal .react-datepicker__navigation-icon--previous::before {
    right: -6px;
  }
  #datepicker-portal .react-datepicker__navigation-icon--next::before {
    left: -6px;
  }
  #datepicker-portal .react-datepicker__day--disabled {
    color: var(--neutral-6) !important;
  }
  #datepicker-portal .react-datepicker__day:hover {
    background: var(--neutral-2);
    border-radius: 8px;
  }
  
  /* Month and Year dropdown styles for portal */
  #datepicker-portal .react-datepicker__month-select,
  #datepicker-portal .react-datepicker__year-select {
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    color: var(--neutral-10);
    padding: 5px;
    border-radius: 4px;
    border: 1px solid var(--neutral-3);
    background-color: var(--white);
  }
  
  /* Year dropdown specific styles for portal */
  #datepicker-portal .react-datepicker__year-dropdown {
    background-color: var(--white);
    border: 1px solid var(--neutral-3);
    border-radius: 8px;
    max-height: 200px;
    overflow-y: auto;
    width: 120px !important;
    left: 0;
    top: 0;
    position: absolute;
    z-index: 1;
  }
  
  #datepicker-portal .react-datepicker__year-dropdown-container {
    position: relative;
  }
  
  #datepicker-portal .react-datepicker__year-option {
    padding: 8px 12px;
    cursor: pointer;
    color: var(--neutral-10);
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
  }
  
  #datepicker-portal .react-datepicker__year-option:hover {
    background-color: var(--neutral-2);
  }
  
  #datepicker-portal .react-datepicker__year-option--selected {
    display: none;
    background-color: var(--primary);
    color: var(--white);
  }
  
  #datepicker-portal .react-datepicker__year-option--selected_year {
    font-weight: 600;
    background-color: var(--primary) !important;
    color: var(--white) !important;
  }
  
  /* Hide the checkbox in year dropdown */
  #datepicker-portal .react-datepicker__year-option .react-datepicker__navigation--years {
    display: inline-block;
    height: 10px;
    width: 10px;
    position: relative;
  }
  
  #datepicker-portal .react-datepicker__year-option .react-datepicker__navigation--years-upcoming {
    top: -4px;
  }
  
  #datepicker-portal .react-datepicker__year-option .react-datepicker__navigation--years-previous {
    top: 4px;
  }
  
  #datepicker-portal .react-datepicker__year-option--selected::before {
    display: none;
  }
  
  /* Hide the checkbox */
  #datepicker-portal .react-datepicker__year-option input[type="checkbox"] {
    display: none;
  }
  
  /* Month dropdown styles */
  #datepicker-portal .react-datepicker__month-dropdown-container,
  .react-datepicker__month-dropdown-container {
    margin: 0 5px;
  }
  
  #datepicker-portal .react-datepicker__month-dropdown,
  .react-datepicker__month-dropdown {
    background-color: var(--white);
    border: 1px solid var(--neutral-3);
    border-radius: 8px;
    max-height: 200px;
    overflow-y: auto;
    width: 120px !important;
  }
  
  #datepicker-portal .react-datepicker__month-option,
  .react-datepicker__month-option {
    padding: 8px 12px;
    cursor: pointer;
    color: var(--neutral-10);
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    position: relative;
  }
  
  #datepicker-portal .react-datepicker__month-option:hover,
  .react-datepicker__month-option:hover {
    background-color: var(--neutral-2);
    color: var(--neutral-10) ;
  }
  
  #datepicker-portal .react-datepicker__month-option--selected,
  .react-datepicker__month-option--selected {
    display: none;
  }
  
  #datepicker-portal .react-datepicker__month-option--selected_month,
  .react-datepicker__month-option--selected_month {
    font-weight: 600;
    background-color: var(--primary) !important;
    color: var(--white) !important;
  }
  
  /* Hide the checkbox in month dropdown */
  #datepicker-portal .react-datepicker__month-option input[type="checkbox"],
  .react-datepicker__month-option input[type="checkbox"] {
    display: none;
  }
  
  /* When both month and year dropdowns are enabled */
  #datepicker-portal .react-datepicker__month-year-dropdown-container,
  .react-datepicker__month-year-dropdown-container {
    display: flex;
    justify-content: center;
    margin: 0 5px;
  }
  
  /* Improve selected option styling */
  #datepicker-portal .react-datepicker__month-option--selected::before,
  .react-datepicker__month-option--selected::before {
    display: none;
  }
  
  /* Improve month option spacing */
  #datepicker-portal .react-datepicker__month-option,
  .react-datepicker__month-option {
    padding: 8px 12px;
    cursor: pointer;
    color: var(--neutral-10);
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    position: relative;
  }
  
  /* Improve year option spacing */
  #datepicker-portal .react-datepicker__year-option,
  .react-datepicker__year-option {
    padding: 8px 12px;
    cursor: pointer;
    color: var(--neutral-10);
    font-weight: 400;
    font-size: 14px;
    line-height: 23px;
    position: relative;
  }

  #datepicker-portal .react-datepicker__month-dropdown-container--scroll,
  #datepicker-portal .react-datepicker__year-dropdown-container--scroll {
    font-size: 12px;
  }

  #datepicker-portal .react-datepicker__month-read-view {
    margin-right: 10px;
  }

  #datepicker-portal .react-datepicker__month-read-view--down-arrow,
  #datepicker-portal .react-datepicker__year-read-view--down-arrow {
    top: 2px;
  }
`;

const Icon = styled.div`
  position: absolute;
  right: 13px;
  top: 38px;
  cursor: pointer;
`;
interface Props extends ReactDatePickerProps {
  label?: string;
  rootClass?: string;
  placeholder: string;
  isRequired?: boolean;
  isDisable?: boolean;
  selectedDate: Date | null;
  onChange: (newDate: Date | null) => void;
  messageError?: string;
  excludeDates?: Date[];
  minDate?: Date | null;
  readOnly?: boolean;
  maxDate?: Date | null;
  portalId?: string;
  extraIconStyle?: React.CSSProperties;
}
const FormInputDate: React.FC<Props> = ({
  label,
  rootClass,
  isRequired = false,
  selectedDate,
  onChange,
  messageError,
  excludeDates,
  minDate,
  readOnly,
  maxDate,
  portalId = '',
  placeholder,
  extraIconStyle,
  ...props
}) => {
  const isOpenDatePicker = useBoolean(false);
  const onChangeInput = (newDate: Date | null) => {
    onChange(newDate);
    isOpenDatePicker.setValue(false);
  };
  return (
    <>
      <DatePickerGlobalStyles />
      <Main className={clsx('fv-row', rootClass)}>
        {label && <Label className={clsx('mb-2', isRequired ? 'required' : '')}>{label}</Label>}
        <div>
          <DatePicker
            placeholderText={placeholder}
            className="form-control form-control-solid mb-3 mb-lg-0 h6 cursor-pointer"
            selected={selectedDate}
            onChange={onChangeInput}
            onInputClick={() => isOpenDatePicker.setValue(!isOpenDatePicker.value)}
            excludeDates={excludeDates}
            dateFormat="dd MMM yyyy"
            open={isOpenDatePicker.value}
            showPopperArrow={false}
            onClickOutside={() => isOpenDatePicker.setValue(false)}
            minDate={minDate}
            readOnly={readOnly}
            maxDate={maxDate}
            portalId={portalId}
            {...props}
          ></DatePicker>
        </div>
        <Icon
          onClick={() => isOpenDatePicker.setValue(!isOpenDatePicker.value)}
          style={extraIconStyle}
        >
          <IconDate />
        </Icon>
        {messageError ? (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{messageError}</span>
            </div>
          </div>
        ) : null}
      </Main>
    </>
  );
};

export { FormInputDate };
